import { datadogRum, type RumEvent, type RumInitConfiguration } from '@datadog/browser-rum';
import { reactPlugin } from '@datadog/browser-rum-react';
import { datadogLogs, LogsEvent } from '@datadog/browser-logs';
import { API_ROUTES } from '@/api/apiRoutes';
import { getAppVersion } from '@/utils/general';
import { isFigmaEmbedContext } from '@/utils/auth.utils';
import { WeaveSurface } from '@/enums/weave-surface.enum';
import { BASE_SERVER_URL } from '@/globals';
import { PAGE_LOAD_ID } from '@/logger/page-load';
import { scrubResourceUrl, scrubText, scrubUrl } from './datadog.utils';

const datadogEnv = import.meta.env.VITE_DATADOG_ENV || (import.meta.env.DEV ? 'development' : import.meta.env.MODE);
// Datadog forwards only from production bundles (import.meta.env.PROD): plain `vite` dev servers,
// vitest, and the e2e dev server are PROD=false. CI's browser-integration job IS a build (PROD=true),
// so it opts out via VITE_DATADOG_LOGS_ENABLED in weavy/app/ci.yml.
const isProductionBundle = import.meta.env.PROD;
const datadogRumEnabled = isProductionBundle && import.meta.env.VITE_DATADOG_ENABLED !== 'false';
const datadogLogsEnabled =
  isProductionBundle && (datadogRumEnabled || import.meta.env.VITE_DATADOG_LOGS_ENABLED === 'true');

// Only a valid http(s) origin is kept; a scheme-less/blank value yields null (dropped from the list, so
// it fails closed to redaction) rather than the string "null" that would match an opaque data: origin.
const toHttpOrigin = (value: string): string | null => {
  try {
    const url = new URL(value);
    return url.protocol === 'https:' || url.protocol === 'http:' ? url.origin : null;
  } catch {
    return null;
  }
};

// Resource URLs from our own API and our own page (both serve only our endpoints / public static
// assets) are kept for timing; every other origin — a third-party image CDN, a signed-asset host — is
// reduced to its origin so a replayable result locator can't reach RUM or Logs.
const firstPartyOrigins = [
  toHttpOrigin(BASE_SERVER_URL),
  typeof window !== 'undefined' ? toHttpOrigin(window.location.origin) : null,
].filter((origin): origin is string => origin !== null);

const sharedConfig = {
  site: 'datadoghq.com',
  service: 'weave_app_client',
  env: datadogEnv,
  version: getAppVersion(),
  sessionSampleRate: 100,
} as const;

const scrubView = (view?: { url?: string; referrer?: string }): void => {
  if (!view) return;
  if (view.url) view.url = scrubUrl(view.url) ?? view.url;
  if (view.referrer) view.referrer = scrubUrl(view.referrer) ?? view.referrer;
};

type ScrubbableError = {
  message?: string;
  stack?: string;
  handling_stack?: string;
  causes?: Array<{ message?: string; stack?: string }>;
};

// Keeps a first-party URL (our API / our own page, scrubbed for params) and reduces every other resource
// URL to its origin. The one redactor for both RUM (resource + LCP events) and Logs: a `forwardErrorsToLogs`
// network log's `http.url`/message and an app `logger.error(..., url)` message all carry a generated-result
// URL whose path is the replayable locator, which the param-only `scrubUrl` would leave intact.
const scrubResource = (url: string): string | undefined => scrubResourceUrl(url, firstPartyOrigins);

// Shared by the Logs and RUM pipelines: a `logger.error(..., url)` is delivered to both (RUM auto-collects
// the console.error copy as an error event), so error text is scrubbed on both intakes. The message fields
// carry the same result URLs as `event.message`, so they go through `scrubResource`; stacks stay on the
// param-only `scrubUrl` so a third-party frame's file/line survives.
const scrubErrorFields = (error?: ScrubbableError): void => {
  if (!error) return;
  if (error.message) error.message = scrubText(error.message, scrubResource);
  if (error.stack) error.stack = scrubText(error.stack);
  if (error.handling_stack) error.handling_stack = scrubText(error.handling_stack);
  for (const cause of error.causes ?? []) {
    if (cause.message) cause.message = scrubText(cause.message, scrubResource);
    if (cause.stack) cause.stack = scrubText(cause.stack);
  }
};

const scrubLogsEvent = (event: LogsEvent): void => {
  scrubView(event.view);
  if (event.http) {
    event.http.url = scrubResource(event.http.url) ?? event.http.url;
  }
  // `location` and `stacktrace` are custom attributes the app's log middleware adds (not typed on
  // LogsEvent); scrub them too so signed URLs in a stack don't slip past `event.error.stack`.
  const attrs = event as { location?: string; stacktrace?: string };
  if (typeof attrs.location === 'string') {
    attrs.location = scrubUrl(attrs.location) ?? attrs.location;
  }
  if (typeof attrs.stacktrace === 'string') {
    attrs.stacktrace = scrubText(attrs.stacktrace);
  }
  if (typeof event.message === 'string') {
    event.message = scrubText(event.message, scrubResource);
  }
  scrubErrorFields(event.error);
};

const isFigmaEmbed = isFigmaEmbedContext();

// Inside figma.com's cross-origin iframe, RUM's default (SameSite=strict) cookie is blocked by the
// browser, so no session gets created and nothing is sent. In the embed, opt into a Partitioned
// (CHIPS) cookie plus a localStorage fallback.
const embedSessionConfig: Partial<RumInitConfiguration> = isFigmaEmbed
  ? {
      usePartitionedCrossSiteSessionCookie: true,
      sessionPersistence: ['cookie', 'local-storage'],
    }
  : {};

const PATHS_TO_IGNORE = ['/collect', '/cdn-cgi/rum', API_ROUTES.bi.track()];

const weaveSurface = isFigmaEmbed ? WeaveSurface.FIGMA_EMBED : WeaveSurface.STANDALONE;

if (datadogRumEnabled) {
  datadogRum.init({
    ...sharedConfig,
    applicationId: import.meta.env.VITE_DATADOG_APPLICATION_ID,
    clientToken: import.meta.env.VITE_DATADOG_RUM_CLIENT_TOKEN,
    sessionReplaySampleRate: 0,
    ...embedSessionConfig,
    trackResources: true,
    trackUserInteractions: true,
    trackLongTasks: true,
    allowedTracingUrls: [BASE_SERVER_URL],
    plugins: [reactPlugin({ router: false })],
    beforeSend: (event: RumEvent) => {
      scrubView((event as { view?: { url?: string; referrer?: string } }).view);
      if (event.type === 'resource' && event.resource?.url) {
        event.resource.url = scrubResource(event.resource.url) ?? event.resource.url;
      }
      if (event.type === 'error') {
        scrubErrorFields((event as { error?: ScrubbableError }).error);
      }
      // The LCP element URL on a view event is the same replayable locator when the page's largest paint
      // is a generated image, so redact it too.
      if (event.type === 'view') {
        const lcp = event.view.performance?.lcp;
        if (lcp?.resource_url) {
          lcp.resource_url = scrubResource(lcp.resource_url) ?? lcp.resource_url;
        }
      }
      return true;
    },
  });

  datadogRum.setGlobalContextProperty('weave_surface', weaveSurface);
  datadogRum.setGlobalContextProperty('pageLoadId', PAGE_LOAD_ID);
}

if (datadogLogsEnabled) {
  datadogLogs.init({
    ...sharedConfig,
    clientToken: import.meta.env.VITE_DATADOG_LOGS_CLIENT_TOKEN,
    forwardErrorsToLogs: true,
    beforeSend: (event: LogsEvent) => {
      const isWeaveApi =
        event.http?.url?.includes('weavy.ai') ||
        event.http?.url?.includes('api.weave.staging.figma.com') ||
        event.http?.url?.includes('api.weave.figma.com');
      if (event.http && event.http.status_code === 0 && !isWeaveApi) return false;
      if (event.http?.url && PATHS_TO_IGNORE.some((p) => event.http?.url.includes(p))) {
        return false;
      }
      scrubLogsEvent(event);
      return true;
    },
  });

  datadogLogs.setGlobalContextProperty('weave_surface', weaveSurface);
  datadogLogs.setGlobalContextProperty('pageLoadId', PAGE_LOAD_ID);
}

/**
 * Records a Datadog RUM custom action on the current session. No-op unless RUM is initialized (a
 * production bundle), so dev/test builds don't emit into an uninitialized SDK.
 */
export const addRumAction = (name: string, context: Record<string, unknown>): void => {
  if (!datadogRumEnabled) return;
  datadogRum.addAction(name, context);
};
